import { gql } from '@apollo/client';

const USER_RESPONSE = `_id
email
role
firstName
lastName
createdAt
updatedAt
isActive
employeeId
mobileNumber
stores
clusterwiseDashboard { is_clusterwise_dashboard assignedBy assignedAt }
clusterwiseTargets { is_clusterwise_targets assignedBy assignedAt }
slabwiseDashboard { is_slabwise_dashboard assignedBy assignedAt }
slabwiseTargets { is_slabwise_targets assignedBy assignedAt }
storeIncentivesDashboard { is_store_dashboard assignedBy assignedAt }
storeIncentivesTargetUpload { is_store_target_upload assignedBy assignedAt }
storeIncentivesAssignedStaffs { is_store_assigned_staffs assignedBy assignedAt }
tradersDashboard { is_traders_dashboard assignedBy assignedAt }
tradersRedemptions { is_traders_redemptions assignedBy assignedAt }
tradersRcMapping { is_traders_rc_mapping assignedBy assignedAt }
tradersApprovals { is_traders_approvals assignedBy assignedAt }`;

export const AUTH_ME = gql`
  query authMe {
    authMe {
      ${USER_RESPONSE}
      accessiblePermissions {
        permissionKey
        permissionName
      }
      storeDetails {
        storeCode
        storeName
      }
    }
  }
`;

export const LOGIN = gql`
  mutation loginUser($input: LoginUserInput!) {
    loginUser(input: $input) {
      token
      user {
       ${USER_RESPONSE}
      }
    }
  }
`;

export const REGISTER = gql`
  mutation createUser($input: CreateUserInput!) {
    createUser(input: $input){
      user {
        ${USER_RESPONSE}
      }
    }
  }
`;

export const UPDATE_USER = gql`
  mutation updateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      user {
        _id
        email
        role
        firstName
        lastName
        createdAt
        updatedAt
        isActive
        employeeId
        stores
        subStores
        budgetCategories
        allowedIps
        mobileNumber
        canCreateOrder
        canCreateUser
        canCreatePayment
        canApplyCoupons
        viewAnalytics
      }
    }
  }
`;

export const GET_USERS = gql`
  query {
    getUsers {
      items {
        _id
        email
        role
        firstName
        lastName
        createdAt
        updatedAt
        isActive
        stores
        employeeId
        mobileNumber
      }
    }
  }
`;

// The users directory as ticket screens need it: who is who, nothing more.
export const GET_USER_DIRECTORY = gql`
  query getUserDirectory {
    getUsers {
      items {
        _id
        email
        role
        firstName
        lastName
        isActive
        stores
        employeeId
      }
    }
  }
`;

export const DELETE_USER = gql`
  mutation deleteUser($input: DeleteUserAccount!) {
    deleteUser(input: $input)
  }
`;

export const GET_USER_DETAILS_BY_ID = gql`
  query getUserDetailsById($input: UserDetailsInput!) {
    getUserDetailsById(input: $input) {
      _id
      email
      role
      firstName
      lastName
      createdAt
      updatedAt
      isActive
      employeeId
      stores
      subStores
      budgetCategories
      allowedMethods
      allowedIps
      photo
      department
      country
      mobileNumber
      canCreateOrder
      canCreateUser
      canCreatePayment
      canApplyCoupons
      viewAnalytics
      ppp {
        is_ppp
        assignedBy
        assignedAt
      }
      ddpp {
        is_ddpp
        assignedBy
        assignedAt
      }
      holidayHomes {
        is_hh
        assignedBy
        assignedAt
      }
      consultant {
        is_consultant
        assignedBy
        assignedAt
      }
      clusterwiseDashboard {
        is_clusterwise_dashboard
        assignedBy
        assignedAt
      }
      clusterwiseTargets {
        is_clusterwise_targets
        assignedBy
        assignedAt
      }
      slabwiseDashboard {
        is_slabwise_dashboard
        assignedBy
        assignedAt
      }
      slabwiseTargets {
        is_slabwise_targets
        assignedBy
        assignedAt
      }
      storeIncentivesDashboard {
        is_store_dashboard
        assignedBy
        assignedAt
      }
      storeIncentivesTargetUpload {
        is_store_target_upload
        assignedBy
        assignedAt
      }
      storeIncentivesAssignedStaffs {
        is_store_assigned_staffs
        assignedBy
        assignedAt
      }
      tradersDashboard {
        is_traders_dashboard
        assignedBy
        assignedAt
      }
      tradersRedemptions {
        is_traders_redemptions
        assignedBy
        assignedAt
      }
      tradersRcMapping {
        is_traders_rc_mapping
        assignedBy
        assignedAt
      }
      tradersApprovals {
        is_traders_approvals
        assignedBy
        assignedAt
      }
    }
  }
`;

export const ASSIGN_PPP_STAFF = gql`
  mutation assignPppStaff($input: AssignPppStaffInput!) {
    assignPppStaff(input: $input) {
      _id
      email
      ppp {
        is_ppp
        assignedBy
        assignedAt
      }
      ddpp {
        is_ddpp
        assignedBy
        assignedAt
      }
      holidayHomes {
        is_hh
        assignedBy
        assignedAt
      }
      consultant {
        is_consultant
        assignedBy
        assignedAt
      }
      clusterwiseDashboard {
        is_clusterwise_dashboard
        assignedBy
        assignedAt
      }
      clusterwiseTargets {
        is_clusterwise_targets
        assignedBy
        assignedAt
      }
      slabwiseDashboard {
        is_slabwise_dashboard
        assignedBy
        assignedAt
      }
      slabwiseTargets {
        is_slabwise_targets
        assignedBy
        assignedAt
      }
      storeIncentivesDashboard {
        is_store_dashboard
        assignedBy
        assignedAt
      }
      storeIncentivesTargetUpload {
        is_store_target_upload
        assignedBy
        assignedAt
      }
      storeIncentivesAssignedStaffs {
        is_store_assigned_staffs
        assignedBy
        assignedAt
      }
      tradersDashboard {
        is_traders_dashboard
        assignedBy
        assignedAt
      }
      tradersRedemptions {
        is_traders_redemptions
        assignedBy
        assignedAt
      }
      tradersRcMapping {
        is_traders_rc_mapping
        assignedBy
        assignedAt
      }
      tradersApprovals {
        is_traders_approvals
        assignedBy
        assignedAt
      }
    }
  }
`;

export const UNASSIGN_PPP_STAFF = gql`
  mutation unassignPppStaff($input: UnassignPppStaffInput!) {
    unassignPppStaff(input: $input) {
      _id
      email
      ppp {
        is_ppp
        assignedBy
        assignedAt
      }
      ddpp {
        is_ddpp
        assignedBy
        assignedAt
      }
      holidayHomes {
        is_hh
        assignedBy
        assignedAt
      }
      consultant {
        is_consultant
        assignedBy
        assignedAt
      }
      clusterwiseDashboard {
        is_clusterwise_dashboard
        assignedBy
        assignedAt
      }
      clusterwiseTargets {
        is_clusterwise_targets
        assignedBy
        assignedAt
      }
      slabwiseDashboard {
        is_slabwise_dashboard
        assignedBy
        assignedAt
      }
      slabwiseTargets {
        is_slabwise_targets
        assignedBy
        assignedAt
      }
      storeIncentivesDashboard {
        is_store_dashboard
        assignedBy
        assignedAt
      }
      storeIncentivesTargetUpload {
        is_store_target_upload
        assignedBy
        assignedAt
      }
      storeIncentivesAssignedStaffs {
        is_store_assigned_staffs
        assignedBy
        assignedAt
      }
      tradersDashboard {
        is_traders_dashboard
        assignedBy
        assignedAt
      }
      tradersRedemptions {
        is_traders_redemptions
        assignedBy
        assignedAt
      }
      tradersRcMapping {
        is_traders_rc_mapping
        assignedBy
        assignedAt
      }
      tradersApprovals {
        is_traders_approvals
        assignedBy
        assignedAt
      }
    }
  }
`;

export const RESET_PASSWORD = gql`
  mutation resetPassword($input: ResetPasswordInput!) {
    resetPassword(input: $input)
  }
`;
